import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, FlaskConical, Trash2, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth";

const COLS = [
  { id: "new", label: "Neu" },
  { id: "in_progress", label: "In Arbeit" },
  { id: "ready", label: "Bereit zur Abholung" },
  { id: "delivered", label: "Abgeholt" },
];

const empty = { patient_name: "", prescription: "", notes: "", status: "new", due_date: "" };

export default function RecipesPage() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(empty);
  const [syncing, setSyncing] = useState(false);
  const { isManager } = useAuth();

  const load = async () => setItems((await api.get("/recipes")).data);
  useEffect(() => { load(); }, []);

  const syncAposoft = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/aposoft/sync-recipes");
      toast.success(`${data.created} neue Rezepturen aus Aposoft importiert`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aposoft-Sync fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    try { if (dialog === "create") await api.post("/recipes", form); else await api.put(`/recipes/${form.id}`, form); toast.success("Gespeichert"); setDialog(null); await load(); }
    catch (err) { toast.error("Fehler"); }
  };
  const moveTo = async (item, status) => { try { await api.put(`/recipes/${item.id}`, { ...item, status }); await load(); } catch (err) { toast.error("Fehler"); } };
  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/recipes/${id}`); await load(); };

  return (
    <div className="space-y-6" data-testid="recipes-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Apotheke</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Rezepturen</h2>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <Button data-testid="rec-aposoft-sync" variant="outline" onClick={syncAposoft} disabled={syncing} className="rounded-full gap-2">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchronisiert…" : "Aposoft-Sync"}
            </Button>
          )}
          <Button data-testid="rec-add-btn" onClick={() => { setForm(empty); setDialog("create"); }} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Neue Rezeptur</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {COLS.map((col) => (
          <div key={col.id} className="space-y-2">
            <div className="flex justify-between items-center px-1">
              <h3 className="font-heading font-bold text-sm">{col.label}</h3>
              <Badge variant="secondary" className="text-xs">{items.filter(i => i.status === col.id).length}</Badge>
            </div>
            <div className="space-y-2">
              {items.filter(i => i.status === col.id).map((i) => (
                <Card key={i.id} data-testid={`rec-card-${i.id}`} className="card-hover border-border/60">
                  <CardContent className="p-3">
                    <div className="font-semibold text-sm flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5 text-blue-600" />{i.patient_name}</div>
                    <div className="text-xs mt-1 text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{i.prescription}</div>
                    {i.due_date && <div className="text-xs text-amber-600 mt-1">Fällig: {i.due_date}</div>}
                    <div className="flex gap-1 mt-2 pt-2 border-t border-border/60">
                      <Select value={i.status} onValueChange={(v) => moveTo(i, v)}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setForm(i); setDialog("edit"); }}>✎</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(i.id)}><Trash2 className="w-3 h-3 text-rose-600" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog === "create" ? "Neue Rezeptur" : "Rezeptur bearbeiten"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Patient</Label><Input data-testid="rec-form-patient" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} /></div>
            <div><Label>Rezeptur / Anweisung</Label><Textarea data-testid="rec-form-prescription" rows={4} value={form.prescription} onChange={(e) => setForm({ ...form, prescription: e.target.value })} /></div>
            <div><Label>Notizen</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <div><Label>Fällig bis</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button data-testid="rec-form-save" onClick={save}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
